// Scout writing {{personal_line}} for several people, one call each, with one retry of the misses.
// No React here, so `node --test` covers it: the tab passes how to read the newest builder and
// how to apply an update to it.
//
// Each line lands as an update on whatever the builder is by then, never as a whole state built
// before the fill started, so ticks and step edits made while Scout writes are kept.
import type { SequenceStep } from '../api/types.ts';
import { setPersonalLine } from './sequenceBuilder.ts';
import type { BuilderState } from './sequenceBuilder.ts';

export interface WrittenLine {
  personal_line?: string | null;
  declined_reason?: string | null;
  rule_note?: string | null;
}

export interface LineFillDeps {
  // The builder as it is now (edits made during the fill included).
  latest: () => BuilderState;
  // Apply one change to the builder as it is when the change lands.
  apply: (update: (state: BuilderState) => BuilderState) => void;
  writeLine: (contactId: string, steps: SequenceStep[]) => Promise<WrittenLine>;
  // Who Scout is writing for right now, null when done.
  onWriting: (contactId: string | null) => void;
}

export interface LineFillResult {
  // Contact id → why there is still no line, after the retry.
  failed: Record<string, string>;
  // Rule notes from the draft path, keyed by contact id.
  notes: Record<string, string>;
}

async function writeOnce(contactIds: string[], deps: LineFillDeps, result: LineFillResult): Promise<void> {
  for (const contactId of contactIds) {
    deps.onWriting(contactId);
    try {
      const written = await deps.writeLine(contactId, deps.latest().steps);
      if (written.rule_note) result.notes[contactId] = written.rule_note;
      const line = written.personal_line;
      if (line) {
        deps.apply(current => setPersonalLine(current, contactId, line));
        delete result.failed[contactId];
      } else {
        result.failed[contactId] = written.declined_reason ?? 'no line';
      }
    } catch (error) {
      result.failed[contactId] = error instanceof Error && error.message ? error.message : 'drafting failed';
    }
  }
}

/** Write each person's line, then retry the misses once. */
export async function fillLinesWithRetry(contactIds: string[], deps: LineFillDeps): Promise<LineFillResult> {
  const result: LineFillResult = { failed: {}, notes: {} };
  await writeOnce(contactIds, deps, result);
  const retryIds = Object.keys(result.failed);
  if (retryIds.length) await writeOnce(retryIds, deps, result);
  deps.onWriting(null);
  return result;
}
